/**
 * Collapsible "Customer Preferences" section. The field set depends on the
 * customer interest — three distinct variants:
 *   - Buy         -> Price Range (min/max) + Mileage Range (min/max)
 *   - Sell/Trade  -> Odometer, Title Type, Condition, Expected Price,
 *                    Payoff Amount, Mileage  (trade-in appraisal)
 *   - Service     -> Odometer, Plate, Unit Number, Factory Warranty
 *                    (months/miles), Extended Warranty (months/miles)
 * All three end with a notes textarea.
 *
 * Persistence: Price Range min / Expected Price map to `budget`; notes map to
 * `description`; `condition` + `mileage` have dedicated columns; the remaining
 * fields ride along in `extra_data` — see BACKEND_GAPS.md and vehiclePayload.js.
 *
 * @module vehicleModal/components/CustomerPreferencesSection
 */
import { Collapse, Form, Input, InputNumber, Select } from "antd";
import React from "react";
import { TRADE_SIDE_INTERESTS, VEHICLE_CONDITION_OPTIONS } from "../constants";
import { nonNegativeNumberRules } from "../utils/vehicleValidation";

const { TextArea } = Input;

const NOTES_LABEL = {
  SERVICE: "Service notes",
  TRADE: "Trade notes",
  SELL: "Additional notes",
  BUY: "Additional notes",
};

/**
 * Buyer preferences: Price Range + Mileage Range.
 */
function BuyPreferences() {
  return (
    <div className="flex flex-row flex-wrap gap-6">
      <Form.Item label="Price Range" className="mb-2">
        <div className="flex flex-row items-center gap-2">
          <Form.Item name="priceRangeMin" noStyle rules={nonNegativeNumberRules("Price")}>
            <InputNumber prefix="$" placeholder="Min" className="w-36" min={0} />
          </Form.Item>
          <span>—</span>
          <Form.Item name="priceRangeMax" noStyle rules={nonNegativeNumberRules("Price")}>
            <InputNumber prefix="$" placeholder="Max" className="w-36" min={0} />
          </Form.Item>
        </div>
      </Form.Item>

      <Form.Item label="Mileage Range" className="mb-2">
        <div className="flex flex-row items-center gap-2">
          <Form.Item name="mileageRangeMin" noStyle rules={nonNegativeNumberRules("Mileage")}>
            <InputNumber suffix="mi" placeholder="Min" className="w-36" min={0} />
          </Form.Item>
          <span>—</span>
          <Form.Item name="mileageRangeMax" noStyle rules={nonNegativeNumberRules("Mileage")}>
            <InputNumber suffix="mi" placeholder="Max" className="w-36" min={0} />
          </Form.Item>
        </div>
      </Form.Item>
    </div>
  );
}

/**
 * Service preferences: the vehicle coming in for service. No appraisal fields —
 * what the service desk needs is how the car is identified on the RO (plate,
 * unit number, odometer) and what warranty still covers the work.
 */
function ServicePreferences() {
  return (
    <>
      <div className="flex flex-row gap-3">
        <Form.Item
          name="odometer"
          label="Odometer"
          className="w-1/3 mb-2"
          rules={nonNegativeNumberRules("Odometer")}
        >
          <InputNumber placeholder="Odometer" className="w-full" min={0} />
        </Form.Item>
        <Form.Item name="plate" label="Plate" className="w-1/3 mb-2">
          <Input placeholder="Plate" />
        </Form.Item>
        <Form.Item name="unitNumber" label="Unit Number" className="w-1/3 mb-2">
          <Input placeholder="Unit Number" />
        </Form.Item>
      </div>
      <div className="flex flex-row gap-3">
        <Form.Item label="Factory Warranty (months / miles)" className="w-1/3 mb-2">
          <div className="flex flex-row items-center gap-2">
            <Form.Item
              name="factoryWarrantyMonths"
              noStyle
              rules={nonNegativeNumberRules("Factory warranty months")}
            >
              <InputNumber placeholder="Months" className="w-full" min={0} />
            </Form.Item>
            <span>/</span>
            <Form.Item
              name="factoryWarrantyMiles"
              noStyle
              rules={nonNegativeNumberRules("Factory warranty miles")}
            >
              <InputNumber placeholder="Miles" className="w-full" min={0} />
            </Form.Item>
          </div>
        </Form.Item>
        <Form.Item label="Extended Warranty (months / miles)" className="w-1/3 mb-2">
          <div className="flex flex-row items-center gap-2">
            <Form.Item
              name="extendedWarrantyMonths"
              noStyle
              rules={nonNegativeNumberRules("Extended warranty months")}
            >
              <InputNumber placeholder="Months" className="w-full" min={0} />
            </Form.Item>
            <span>/</span>
            <Form.Item
              name="extendedWarrantyMiles"
              noStyle
              rules={nonNegativeNumberRules("Extended warranty miles")}
            >
              <InputNumber placeholder="Miles" className="w-full" min={0} />
            </Form.Item>
          </div>
        </Form.Item>
        {/* Keeps the two warranty groups on the same 1/3 grid as the row above. */}
        <div className="w-1/3" />
      </div>
    </>
  );
}

/**
 * Sell/Trade preferences: appraising a vehicle the customer already owns.
 */
function TradeSidePreferences() {
  return (
    <>
      <div className="flex flex-row gap-3">
        <Form.Item name="odometer" label="Odometer" className="w-1/3 mb-2">
          <InputNumber placeholder="Odometer" className="w-full" min={0} />
        </Form.Item>
        <Form.Item name="titleType" label="Title Type" className="w-1/3 mb-2">
          <Input placeholder="Title Type" />
        </Form.Item>
        <Form.Item name="condition" label="Condition" className="w-1/3 mb-2">
          <Select
            allowClear
            placeholder="Condition"
            options={VEHICLE_CONDITION_OPTIONS.map(c => ({ value: c, label: c }))}
          />
        </Form.Item>
      </div>
      <div className="flex flex-row gap-3">
        <Form.Item
          name="expectedPrice"
          label="Expected Price"
          className="w-1/3 mb-2"
          rules={nonNegativeNumberRules("Expected Price")}
        >
          <InputNumber prefix="$" placeholder="0.00" className="w-full" min={0} />
        </Form.Item>
        <Form.Item
          name="payoffAmount"
          label="Payoff Amount"
          className="w-1/3 mb-2"
          rules={nonNegativeNumberRules("Payoff Amount")}
        >
          <InputNumber prefix="$" placeholder="0.00" className="w-full" min={0} />
        </Form.Item>
        <Form.Item
          name="mileage"
          label="Mileage"
          className="w-1/3 mb-2"
          rules={nonNegativeNumberRules("Mileage")}
        >
          <InputNumber suffix="MPG" placeholder="0" className="w-full" min={0} />
        </Form.Item>
      </div>
    </>
  );
}

/**
 * @param {Object} props
 * @param {import("../types").CustomerInterest} props.customerInterest
 */
export default function CustomerPreferencesSection({ customerInterest }) {
  const isService = customerInterest === "SERVICE";
  const isTradeSide = TRADE_SIDE_INTERESTS.includes(customerInterest);
  // Sell/Trade use the generic "Additional notes" label (per design); Buy and
  // Service keep the type-specific label.
  const notesLabel = isTradeSide
    ? "Additional notes"
    : NOTES_LABEL[customerInterest] || "Additional notes";

  const items = [
    {
      key: "preferences",
      label: "Customer Preferences",
      // Always mount so the fields register/save even if the panel is collapsed.
      forceRender: true,
      children: (
        <div className="flex flex-col gap-1">
          {isService ? (
            <ServicePreferences />
          ) : isTradeSide ? (
            <TradeSidePreferences />
          ) : (
            <BuyPreferences />
          )}

          <Form.Item name="additionalNotes" label={notesLabel} className="w-full mb-0">
            <TextArea rows={3} placeholder={notesLabel} />
          </Form.Item>
        </div>
      ),
    },
  ];

  return (
    <Collapse
      defaultActiveKey={["preferences"]}
      items={items}
      expandIconPosition="end"
      className="vehicle-section-collapse mt-6"
    />
  );
}
